import {Injectable} from '@angular/core';
import {ApiHelperService, ApiResult} from './api-helper.service';
import {API} from 'aws-amplify';
import {Race, RaceRankingMethod, RaceType} from '../model/resources/race';
import {OverlayService} from './overlay.service';
import {AuthService} from './auth.service';
import {MatDialog} from '@angular/material/dialog';
import {MessageDialogComponent} from '../../views/partials/message-dialog/message-dialog.component';
import {LinkageService} from '../model/resources/tatta-user';

@Injectable({
  providedIn: 'root'
})
export class RaceApiService {

  constructor(
    private apiHelperService: ApiHelperService,
    private authService: AuthService,
    private dialog: MatDialog,
    private overlayService: OverlayService,
  ) {
  }

  async create(createRaceInput: CreateRaceInput): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowManageRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers,
      body: createRaceInput
    };

    try {
      const result = await this.overlayService.call(API.post(this.apiHelperService.apiName, 'races', init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

  /**
   *
   * @param searchId ID
   * @param raceType NormalRace, VirtualRace
   * @param searchType SEARCH_BY_RUNNET_ID、SEARCH_BY_MOSHICOM_ID、SEARCH_BY_ID
   */
  async searchById(searchId: number, raceType: RaceType, searchType: RaceSearchType): Promise<ApiResult> {
    return this.search({
      searchType,
      raceType,
      searchId
    });
  }

  async searchByRankingTerm(searchTime: number, raceType: RaceType): Promise<ApiResult> {
    return this.search({
      searchType: RaceSearchType.SEARCH_BY_RANKING_TERM,
      raceType,
      searchTime
    });
  }

  async searchByQuery(query: string, raceType: RaceType): Promise<ApiResult> {
    return this.search({
      searchType: RaceSearchType.SEARCH_BY_QUERY,
      raceType,
      query
    });
  }

  async searchByTotalRaceId(query: string, raceType: RaceType): Promise<ApiResult> {
    return this.search({
      searchType: RaceSearchType.SEARCH_BY_TOTAL_RACE_ID,
      raceType,
      query
    });
  }

  async searchNotImportedRaces(runtesId: string): Promise<ApiResult> {
    return this.search({
      searchType: RaceSearchType.SEARCH_BY_NOT_IMPORTED,
      raceType: RaceType.NormalRace,
      query: runtesId
    });
  }

  private async search(input: GetRaceListInput): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowViewRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers,
      queryStringParameters: input
    };

    try {
      const result = await this.overlayService.call(API.get(this.apiHelperService.apiName, 'races', init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }


  async getById(raceId: number): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowViewRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers
    };

    try {
      const result = await this.overlayService.call(API.get(this.apiHelperService.apiName, `races/${raceId}`, init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

  /**
   * VirtualRace向け
   * 更新、又は大会からイベントへの移動に利用
   */
  async update(id: number, updateRaceInput: UpdateRaceInput): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowManageRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers,
      body: updateRaceInput
    };

    try {
      const result = await this.overlayService.call(API.put(this.apiHelperService.apiName, `races/${id}`, init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

  /**
   * 大会向け
   */
  async patch(id: number, patchRaceInput: PatchRaceInput): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowManageRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers,
      body: patchRaceInput
    };
    try {
      const result = await this.overlayService.call(API.patch(this.apiHelperService.apiName, `races/${id}`, init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

  async import(race: Race): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowManageRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers,
      body: race
    };
    try {
      const result = await this.overlayService.call(API.post(this.apiHelperService.apiName, 'races/import', init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

  async getRunnetOneRaceDetail(raceId: number): Promise<ApiResult> {
    if (!this.authService.userAuth?.isAllowManageRaces()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return this.apiHelperService.getErrorResultByResponse(undefined);
    }
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers
    };

    try {
      const result = await this.overlayService.call(API.get(this.apiHelperService.oneApiName, `v1/one/races/${raceId}`, init));
      return this.apiHelperService.getSuccessResultByResponse(result);
    } catch (e: any) {
      return this.apiHelperService.getErrorResultByResponse(e);
    }
  }

}

export interface GetRaceListInput {
  searchType: RaceSearchType;
  raceType: RaceType;
  query?: string;
  searchId?: number;
  searchTime?: number;
}

export enum RaceSearchType {
  SEARCH_BY_QUERY,
  SEARCH_BY_RUNNET_ID,
  SEARCH_BY_MOSHICOM_ID,
  SEARCH_BY_RANKING_TERM,
  SEARCH_BY_NOT_IMPORTED,
  SEARCH_BY_ID,
  SEARCH_BY_TOTAL_RACE_ID,
}

export interface UpdateRaceInput {
  title: string;
  subtitle: string;
  eventDescription: string;
  eventOutline: string;
  eventRule: string;
  eventPrizesJson: string;
  rankingByRunning: boolean;
  rankingByTrailRunning: boolean;
  rankingByCycling: boolean;
  rankingByWalking: boolean;
  rankingByHiking: boolean;
  rankingTargetMinPace: number | null | undefined;
  beginningRankingTerm: number;
  endOfRankingTerm: number;
  beginningOpenDate: number;
  endOfOpenDate: number;
  beginningEntryTerm: number;
  endOfEntryTerm: number;
  eventImageId: string | null;
  eventImageWidth: number | null;
  eventImageHeight: number | null;
  eventImageFileName: string | null;
  eventImageContentType: string | null;
  eventImageContentSize: number | null;
  joinEventImageId: string | null;
  joinEventImageWidth: number | null;
  joinEventImageHeight: number | null;
  joinEventImageFileName: string | null;
  joinEventImageContentType: string | null;
  joinEventImageContentSize: number | null;
  rankingMethod: RaceRankingMethod;
  isPublished: boolean;
  requiredServices: LinkageService[];
}

export interface CreateRaceInput extends UpdateRaceInput {
  fromRunnetTotalRaceCode: string | null;
  fromMoshicomRaceId: number | null;
}

export interface PatchRaceInput {
  title: string;
  rankingByRunning: boolean;
  rankingByTrailRunning: boolean;
  rankingByCycling: boolean;
  rankingByWalking: boolean;
  rankingByHiking: boolean;
  rankingTargetMinPace: number | null | undefined;
  beginningRankingTerm: number;
  endOfRankingTerm: number;
  location: string;
  detailUrl: string;
}

